import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/Dialog/Dialog'
import { Button } from '@/components/ui/Button/Button'
import { PERMISSIONS_HELP_URL } from '@/constants/meeting'

type PermissionWarningModalProps = {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  cameraPermissionDenied: boolean
  microphonePermissionDenied: boolean
}

export const PermissionWarningModal = ({
  isOpen,
  onClose,
  onConfirm,
  cameraPermissionDenied,
  microphonePermissionDenied
}: PermissionWarningModalProps) => {
  const handleHelpClick = () => {
    window.open(PERMISSIONS_HELP_URL, '_blank', 'noopener,noreferrer')
  }

  // Determine the message based on which permissions are not granted
  // cameraPermissionDenied means camera is NOT granted (denied or prompt)
  // microphonePermissionDenied means microphone is NOT granted (denied or prompt)
  const getWarningMessage = () => {
    if (cameraPermissionDenied && microphonePermissionDenied) {
      return 'ne pourra pas vous voir ni vous entendre'
    } else if (cameraPermissionDenied && !microphonePermissionDenied) {
      // Camera not granted, but microphone is granted
      return 'ne pourra pas vous voir'
    } else if (!cameraPermissionDenied && microphonePermissionDenied) {
      // Microphone not granted, but camera is granted
      return 'ne pourra pas vous entendre'
    }
    return 'ne pourra pas vous voir ni vous entendre' // fallback
  }

  const getPermissionText = () => {
    if (cameraPermissionDenied && microphonePermissionDenied) {
      return 'au micro et à la caméra'
    } else if (cameraPermissionDenied && !microphonePermissionDenied) {
      // Camera not granted, but microphone is granted
      return 'à la caméra'
    } else if (!cameraPermissionDenied && microphonePermissionDenied) {
      // Microphone not granted, but camera is granted
      return 'au micro'
    }
    return 'au micro et à la caméra' // fallback
  }

  const warningMessage = getWarningMessage()
  const permissionText = getPermissionText()

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-left">
            Caméra ou microphone inactif
          </DialogTitle>
          <DialogDescription className="pt-2 text-left text-primary font-light">
            Souhaitez-vous vraiment rejoindre le rendez-vous sans avoir activé les autorisations d&apos;accès {permissionText} ? Votre interlocuteur {warningMessage}.
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="flex-col gap-3 sm:flex-col pt-4">
          <div className="flex w-full gap-3 my-2">
            <Button
              onClick={onClose}
              className="flex-1 border border-primary bg-white text-primary rounded-lg hover:bg-gray-50"
            >
              Non
            </Button>
            <Button
              variant="default"
              onClick={onConfirm}
              className="flex-1 bg-primary text-white hover:opacity-90 rounded-lg"
            >
              Oui
            </Button>
          </div>
          <div className="w-full text-center">
            <button
              onClick={handleHelpClick}
              className="text-sm font-medium text-primary underline"
            >
              J&apos;ai besoin d&apos;aide
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
